import { useState } from 'react';
import {
  IMPORTANCE_LABELS,
  IMPORTANCE_LEVELS,
  MARKER_ZONES,
  WEEKDAY_LABELS,
  importanceMarks,
  type Importance,
  formatMinute,
  parseMinute,
  type TimePoint,
  type TimePointInput,
  type TimeWindow,
  type TimeWindowInput,
} from '@pipyar/core';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Field } from '@/components/ui/Field';
import { PairIcon } from '@/components/PairIcon';
import type { InstrumentDto } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';

const PRESET_COLORS = [
  '#38bdf8',
  '#a78bfa',
  '#f472b6',
  '#fb923c',
  '#facc15',
  '#4ade80',
  '#f87171',
  '#94a3b8',
];

interface CommonProps {
  instruments: InstrumentDto[];
  onCancel: () => void;
  busy?: boolean;
  error?: string | null;
}

/** Shared controls: colour, description, weekdays, pairs, enabled. */
function SharedFields({
  color,
  setColor,
  description,
  setDescription,
  weekdays,
  setWeekdays,
  instrumentIds,
  setInstrumentIds,
  enabled,
  setEnabled,
  timezone,
  setTimezone,
  instruments,
}: {
  color: string;
  setColor: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  weekdays: number[];
  setWeekdays: (v: number[]) => void;
  instrumentIds: number[];
  setInstrumentIds: (v: number[]) => void;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  timezone: string | null;
  setTimezone: (v: string | null) => void;
  instruments: InstrumentDto[];
}) {
  const toggle = (list: number[], value: number, set: (v: number[]) => void) =>
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const { t } = useI18n();

  return (
    <>
      <div className="space-y-2">
        <label htmlFor="marker-tz" className="block text-sm font-medium">
          Timezone
        </label>
        <select
          id="marker-tz"
          value={timezone ?? ''}
          onChange={(e) => setTimezone(e.target.value === '' ? null : e.target.value)}
          className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          {MARKER_ZONES.map((z) => (
            <option key={z.id ?? 'broker'} value={z.id ?? ''}>
              {z.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          {timezone
            ? 'Times are read in this zone, so the marker follows its session through daylight saving.'
            : 'Times are read on the broker clock, which is a fixed offset — a session marker will drift an hour when the clocks change.'}
        </p>
      </div>

      <div className="space-y-2">
        <span className="block text-sm font-medium">{t('settings.colour')}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              aria-label={c}
              aria-pressed={color.toLowerCase() === c}
              className={cn(
                'size-6 rounded-md ring-offset-2 ring-offset-surface transition',
                color.toLowerCase() === c ? 'ring-2 ring-ring' : 'hover:scale-110',
              )}
              style={{ backgroundColor: c }}
            />
          ))}
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            aria-label={t('settings.customColour')}
            className="h-6 w-8 cursor-pointer rounded-md border border-border bg-transparent"
          />
        </div>
      </div>

      <Field htmlFor="marker-desc" label={t('settings.description')} hint="Shown under the name in the sidebar.">
        <Input
          id="marker-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('settings.descriptionPlaceholder')}
        />
      </Field>

      <div className="space-y-2">
        <span className="block text-sm font-medium">Days</span>
        <div className="flex flex-wrap gap-1">
          {WEEKDAY_LABELS.map((label, day) => {
            const on = weekdays.includes(day);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggle(weekdays, day, setWeekdays)}
                className={cn(
                  'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  on ? 'bg-primary/15 text-primary' : 'bg-elevated text-muted-foreground',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {weekdays.length === 0 ? 'Every day.' : 'Only the selected days.'}
        </p>
      </div>

      <div className="space-y-2">
        <span className="block text-sm font-medium">{t('settings.tradingPairs')}</span>
        {instruments.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('settings.noPairs')}</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {instruments.map((i) => {
              const on = instrumentIds.includes(i.id);
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => toggle(instrumentIds, i.id, setInstrumentIds)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                    on ? 'bg-primary/15 text-primary' : 'bg-elevated text-muted-foreground',
                  )}
                >
                  <PairIcon symbol={i.symbol} size={14} />
                  {i.symbol}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {instrumentIds.length === 0
            ? 'Applies to every pair.'
            : 'Only the selected pairs.'}
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="size-4 accent-[var(--primary)]"
        />
        Show on charts
      </label>
    </>
  );
}

// ---------------------------------------------------------------- window

export function WindowForm({
  initial,
  onSubmit,
  instruments,
  onCancel,
  busy,
  error,
}: CommonProps & {
  initial?: TimeWindow;
  onSubmit: (input: TimeWindowInput) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [start, setStart] = useState(formatMinute(initial?.startMinute ?? 180));
  const [end, setEnd] = useState(formatMinute(initial?.endMinute ?? 240));
  const [color, setColor] = useState(initial?.color ?? PRESET_COLORS[0]!);
  const [description, setDescription] = useState(initial?.description ?? '');
  const [weekdays, setWeekdays] = useState<number[]>(initial?.weekdays ?? []);
  const [instrumentIds, setInstrumentIds] = useState<number[]>(initial?.instrumentIds ?? []);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [timezone, setTimezone] = useState<string | null>(initial?.timezone ?? null);
  const [importance, setImportance] = useState<Importance>(initial?.importance ?? 1);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = () => {
    const startMinute = parseMinute(start);
    const endMinute = parseMinute(end);
    if (startMinute === null || endMinute === null) {
      setLocalError('Times must be in HH:MM form.');
      return;
    }
    if (startMinute === endMinute) {
      setLocalError('Start and end cannot be the same time.');
      return;
    }
    if (name.trim().length === 0) {
      setLocalError('Name is required.');
      return;
    }
    setLocalError(null);
    onSubmit({
      name: name.trim(),
      startMinute,
      endMinute,
      color,
      description,
      enabled,
      importance,
      timezone,
      weekdays,
      instrumentIds,
    });
  };

  const { t } = useI18n();

  return (
    <div className="space-y-4">
      <Field htmlFor="w-name" label={t('settings.name')}>
        <Input id="w-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field htmlFor="w-start" label={t('settings.start')}>
          <Input id="w-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
        <Field htmlFor="w-end" label={t('settings.end')}>
          <Input id="w-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </Field>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        Times are in the timezone selected below. An end before the start wraps past midnight.
      </p>

      <div className="space-y-2">
        <span className="block text-sm font-medium">{t('settings.importance')}</span>
        <div className="flex gap-1.5">
          {IMPORTANCE_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => setImportance(level)}
              aria-pressed={importance === level}
              className={cn(
                'flex-1 rounded-lg border px-2 py-1.5 text-center transition-colors',
                importance === level
                  ? 'border-primary/50 bg-primary/12 text-primary'
                  : 'border-border bg-elevated text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="block text-sm font-bold tracking-tight">
                {importanceMarks(level)}
              </span>
              <span className="block text-[10px]">{IMPORTANCE_LABELS[level]}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Higher importance shades the band more strongly on the chart.
        </p>
      </div>

      <SharedFields
        color={color}
        setColor={setColor}
        description={description}
        setDescription={setDescription}
        weekdays={weekdays}
        setWeekdays={setWeekdays}
        instrumentIds={instrumentIds}
        setInstrumentIds={setInstrumentIds}
        enabled={enabled}
        setEnabled={setEnabled}
        timezone={timezone}
        setTimezone={setTimezone}
        instruments={instruments}
      />

      <FormFooter
        error={localError ?? error ?? null}
        busy={busy}
        onCancel={onCancel}
        onSubmit={submit}
        submitLabel={initial ? t('settings.saveWindow') : t('settings.addWindow')}
      />
    </div>
  );
}

// ---------------------------------------------------------------- point

export function PointForm({
  initial,
  onSubmit,
  instruments,
  onCancel,
  busy,
  error,
}: CommonProps & {
  initial?: TimePoint;
  onSubmit: (input: TimePointInput) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [time, setTime] = useState(formatMinute(initial?.minute ?? 180));
  const [color, setColor] = useState(initial?.color ?? PRESET_COLORS[1]!);
  const [description, setDescription] = useState(initial?.description ?? '');
  const [weekdays, setWeekdays] = useState<number[]>(initial?.weekdays ?? []);
  const [instrumentIds, setInstrumentIds] = useState<number[]>(initial?.instrumentIds ?? []);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [timezone, setTimezone] = useState<string | null>(initial?.timezone ?? null);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = () => {
    const minute = parseMinute(time);
    if (minute === null) {
      setLocalError('Time must be in HH:MM form.');
      return;
    }
    if (name.trim().length === 0) {
      setLocalError('Name is required.');
      return;
    }
    setLocalError(null);
    onSubmit({
      name: name.trim(),
      minute,
      color,
      description,
      enabled,
      timezone,
      weekdays,
      instrumentIds,
    });
  };

  const { t } = useI18n();

  return (
    <div className="space-y-4">
      <Field htmlFor="p-name" label={t('settings.name')}>
        <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>

      <Field htmlFor="p-time" label={t('settings.time')} hint="In the timezone selected below.">
        <Input id="p-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </Field>

      <SharedFields
        color={color}
        setColor={setColor}
        description={description}
        setDescription={setDescription}
        weekdays={weekdays}
        setWeekdays={setWeekdays}
        instrumentIds={instrumentIds}
        setInstrumentIds={setInstrumentIds}
        enabled={enabled}
        setEnabled={setEnabled}
        timezone={timezone}
        setTimezone={setTimezone}
        instruments={instruments}
      />

      <FormFooter
        error={localError ?? error ?? null}
        busy={busy}
        onCancel={onCancel}
        onSubmit={submit}
        submitLabel={initial ? t('settings.savePoint') : t('settings.addPoint')}
      />
    </div>
  );
}

function FormFooter({
  error,
  busy,
  onCancel,
  onSubmit,
  submitLabel,
}: {
  error: string | null;
  busy?: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
}) {

  return (
    <>
      {error && (
        <p role="alert" className="rounded-lg border border-danger/30 bg-danger/8 p-2 text-xs text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </>
  );
}
